import { useGSAP } from '@gsap/react';
import { graphql } from 'gatsby';
import gsap from 'gsap';
import { t } from 'i18next';
import React, { useRef, useState } from 'react';
import { SanityImageType } from '../../../graphql-fragments/SanityImage';
import TranscriptIcon from '../../../images/transcript.inline.svg';
import { BREAKPOINTS } from '../../../styles/breakpoints';
import { ModuleBgColor } from '../../../templates/Page';
import { clsx } from '../../../utils/utils';
import { CommonModuleProps } from '../../ModulesContent';
import Image from '../Image';
import ModuleLayout from '../ModuleLayout';
import Video from '../Video';
import VideoTranscriptionModal from '../VideoTranscriptionModal';
import * as styles from './VideoModule.module.scss';

export const VideoModuleFragment = graphql`
  fragment VideoModule on SanityPageVideoModule {
    title {
      ...LocaleStringFragment
    }
    text {
      ...LocaleTextFragment
    }
    video {
      url {
        ...LocaleUrlFragment
      }
      file {
        ...LocaleFileFragment
      }
      thumbnail {
        ...LocaleImageFragment
      }
      videoTranscription {
        ...LocaleTextFragment
      }
      videoTitle {
        ...LocaleStringFragment
      }
      videoSubtitle {
        ...LocaleStringFragment
      }
    }
    mobileBackgroundImage {
      ...SanityImage
      ...SanityImage
    }
    desktopBackgroundImage {
      ...SanityImage
    }
  }
`;

type VideoAsset =
  | {
      url: string;
      file?: never;
      thumbnail?: {
        asset: {
          _id: string;
          url: string;
        };
      };
    }
  | {
      url?: never;
      file: {
        asset: {
          url: string;
        };
      };
      thumbnail: {
        asset: {
          _id: string;
          url: string;
        };
      };
    };

export type VideoModuleProps = {
  title: string;
  text: string;
  video: VideoAsset & {
    videoTranscription?: string;
    videoTitle?: string;
    videoSubtitle?: string;
  };
  mobileBackgroundImage: SanityImageType;
  desktopBackgroundImage: SanityImageType;
  className?: string;
};

export function getModuleBgColor(props: VideoModuleProps): ModuleBgColor {
  /**
   * The purpose of this function is to let other modules know which background color this module has.
   * Knowing this, we can use this function to make decisions about the layout and spacing between modules.
   */
  return 'colored';
}

function VideoModule(props: VideoModuleProps & CommonModuleProps): React.ReactElement {
  const {
    title,
    text,
    video,
    mobileBackgroundImage,
    desktopBackgroundImage,
    className,
    previousModuleBgColor,
    moduleId,
  } = props;

  const [isVideoTranscriptionModalOpen, setIsVideoTranscriptionModalOpen] = useState(false);

  const containerRef = useRef<HTMLElement | null>(null);

  useGSAP(
    () => {
      gsap
        .timeline({
          scrollTrigger: {
            trigger: `.${styles.contentWrapper}`,
            start: 'top 90%',
          } satisfies ScrollTrigger.Vars,
        })
        // animate text
        .from(`.${styles.title}, .${styles.text}`, {
          opacity: 0,
          y: -20,
          stagger: 0.2,
        })
        .from(
          `.${styles.videoContainer}`,
          {
            y: -80,
            opacity: 0,
            duration: 2,
          },
          '>-0.5',
        )
        .from(
          `.${styles.link}`,
          {
            y: -16,
            opacity: 0,
            duration: 1,
            stagger: 0.2,
          },
          '-=0.4',
        );

      // image parallax
      gsap.fromTo(
        `.${styles.backgroundImageContainer}`,
        {
          y: '-20%',
          transform: 'scale(1.2)',
          ease: 'none',
        },
        {
          y: '20%',
          scrollTrigger: {
            trigger: `.${styles.contentContainer}`,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          } satisfies ScrollTrigger.Vars,
        },
      );
    },
    { scope: containerRef },
  );

  return (
    <ModuleLayout
      id={moduleId}
      className={clsx(className, styles.container)}
      contentClassName={clsx(styles.contentContainer)}
      moduleRef={containerRef}
      currentModuleBgColor={getModuleBgColor(props)}
      previousModuleBgColor={previousModuleBgColor}
    >
      <div className={styles.backgroundImageContainer}>
        <Image
          className={styles.backgroundImage}
          image={mobileBackgroundImage}
          dimensions={[
            [520, 1080],
            [BREAKPOINTS.tabletVertical, desktopBackgroundImage, 1920, 1080],
          ]}
          cover
        />
        <div className={styles.overlay}></div>
      </div>
      <div className={clsx(styles.contentWrapper)}>
        <div className={clsx(styles.textContainer)}>
          <h2 className={clsx(styles.title)}>{title}</h2>
          <p className={styles.text}>{text}</p>
        </div>
        <div className={styles.videoContainer}>
          <Video
            url={video.file ? video.file.asset.url : video.url}
            className={styles.video}
            thumbnail={video.thumbnail}
          />
          {video.videoTranscription && (
            <>
              <button
                onClick={() => {
                  setIsVideoTranscriptionModalOpen(true);
                }}
                className={styles.link}
              >
                {t('hero.button_video_transcript', 'Ver transcrição do vídeo')}{' '}
                <TranscriptIcon className={styles.linkIcon} />
              </button>
              <VideoTranscriptionModal
                isOpen={isVideoTranscriptionModalOpen}
                title={video.videoTitle}
                subtitle={video.videoSubtitle}
                text={video.videoTranscription}
                onClose={() => {
                  setIsVideoTranscriptionModalOpen(false);
                }}
              />
            </>
          )}
        </div>
      </div>
    </ModuleLayout>
  );
}

export default VideoModule;
